// Common HUD style tokens to keep visuals consistent.
// MU Online inspired: dark stone panels, brass-gold ornate borders, deep navy interiors.
import type { CSSProperties } from 'react';

const ASSET = '/assets/ui';

// Re-export raw asset paths so individual panels can compose custom layouts.
export const hudAssets = {
  panelFrame: `${ASSET}/panels/panel_frame.png`,
  panelSmall: `${ASSET}/panels/panel_small.png`,
  headerBar: `${ASSET}/panels/header_bar.png`,
  divider: `${ASSET}/panels/divider.png`,
  tabs: `${ASSET}/panels/tabs.png`,
  scrollbar: `${ASSET}/panels/scrollbar.png`,
  slotsAtlas: `${ASSET}/slots/slots_atlas.png`,
  equipSilhouettes: `${ASSET}/slots/equip_silhouettes.png`,
  buttons: `${ASSET}/buttons/buttons.png`,
  btnClose: `${ASSET}/buttons/btn_close.png`,
};

// Border-image slice values (in source PNG pixels) for 9-slice frames.
// panel_frame.png ~936x936 with ~150px ornamented borders.
const PANEL_SLICE = 150;
const PANEL_SMALL_SLICE = 90;

export const ui = {
  panel: {
    color: '#f3e7c2',
    padding: '28px 26px 22px',
    borderStyle: 'solid',
    borderWidth: `${PANEL_SLICE / 4}px`,
    borderImageSource: `url(${hudAssets.panelFrame})`,
    borderImageSlice: PANEL_SLICE,
    borderImageRepeat: 'stretch',
    borderImageWidth: `${PANEL_SLICE / 4}px`,
    background: 'linear-gradient(180deg, #1a2238 0%, #0e1426 100%)',
    backgroundClip: 'padding-box',
    boxShadow: '0 6px 18px rgba(0,0,0,0.7), inset 0 0 24px rgba(0,0,0,0.55)',
    fontFamily: '"Philosopher", "Trajan Pro", serif',
  } as CSSProperties,
  panelSmall: {
    color: '#f3e7c2',
    padding: '14px 14px 12px',
    borderStyle: 'solid',
    borderWidth: `${PANEL_SMALL_SLICE / 4}px`,
    borderImageSource: `url(${hudAssets.panelSmall})`,
    borderImageSlice: PANEL_SMALL_SLICE,
    borderImageRepeat: 'stretch',
    borderImageWidth: `${PANEL_SMALL_SLICE / 4}px`,
    background: 'linear-gradient(180deg, #1a2238 0%, #0e1426 100%)',
    backgroundClip: 'padding-box',
    boxShadow: '0 4px 12px rgba(0,0,0,0.65), inset 0 0 16px rgba(0,0,0,0.5)',
  } as CSSProperties,
  headerBar: {
    height: 44,
    backgroundImage: `url(${hudAssets.headerBar})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    color: '#fff5cc',
    fontWeight: 700,
    letterSpacing: 1,
    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    fontFamily: '"Philosopher", serif',
  } as CSSProperties,
  divider: {
    height: 14,
    backgroundImage: `url(${hudAssets.divider})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    margin: '8px 0',
  } as CSSProperties,
  primaryBtn: {
    width: 120,
    height: 36,
    backgroundImage: `url(${hudAssets.buttons})`,
    backgroundSize: '300% 100%',
    backgroundPosition: '0% 0%',
    backgroundRepeat: 'no-repeat',
    color: '#fff5cc',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 700,
    letterSpacing: 0.5,
    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
    fontFamily: '"Philosopher", serif',
    backgroundColor: 'transparent',
    transition: 'background-position 0.1s',
  } as CSSProperties,
  secondaryBtn: {
    width: 100,
    height: 32,
    backgroundImage: `url(${hudAssets.buttons})`,
    backgroundSize: '300% 100%',
    backgroundPosition: '0% 0%',
    backgroundRepeat: 'no-repeat',
    color: '#e8eef7',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontFamily: '"Philosopher", serif',
    backgroundColor: 'transparent',
    filter: 'saturate(0.6) brightness(0.85)',
    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
  } as CSSProperties,
  closeBtn: {
    width: 32,
    height: 32,
    backgroundImage: `url(${hudAssets.btnClose})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: 6,
    right: 6,
  } as CSSProperties,
  slot: {
    width: 40,
    height: 40,
    backgroundImage: `url(${hudAssets.slotsAtlas})`,
    backgroundSize: '400% 200%',
    backgroundPosition: '0% 0%',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    position: 'relative',
  } as CSSProperties,
  input: {
    background: 'rgba(11,18,32,0.85)',
    color: '#fff5cc',
    border: '1px solid #6b5320',
    padding: '8px 10px',
    borderRadius: 2,
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: '"Philosopher", serif',
  } as CSSProperties,
};

// Slot atlas helpers: 4 cols x 2 rows.
// Row 0: empty, hover, equipped, locked.  Row 1: rare, epic, legendary, set.
export const slotVariant = {
  empty: { backgroundPosition: '0% 0%' },
  hover: { backgroundPosition: '33.33% 0%' },
  equipped: { backgroundPosition: '66.66% 0%' },
  locked: { backgroundPosition: '100% 0%' },
  rare: { backgroundPosition: '0% 100%' },
  epic: { backgroundPosition: '33.33% 100%' },
  legendary: { backgroundPosition: '66.66% 100%' },
  set: { backgroundPosition: '100% 100%' },
} as const satisfies Record<string, CSSProperties>;

// Button atlas helpers: 3 cols x 1 row.
export const buttonStateBg = {
  normal: { backgroundPosition: '0% 0%' },
  hover: { backgroundPosition: '50% 0%' },
  pressed: { backgroundPosition: '100% 0%' },
} as const;
